import { inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import {
  allowedCountries,
  CLIENT_APP_ID,
  country as Country,
  PhoneNumber,
  getMatchingPhonePattern,
  phonePatterns,
} from '@sc/data-access-models';
import {
  CountryCode,
  PhoneNumber as LibPhoneNumJS,
  parsePhoneNumber,
} from 'libphonenumber-js';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  PhoneNumberDisplayContext,
  PhoneNumberDisplayDefaultKeys,
} from '../models';
import { PHONE_NUMBER_DISPLAY_CONTEXT } from '../tokens';
import { ContextualDisplayService } from './contextual-display.service';

@Injectable({ providedIn: 'root' })
export class PhoneNumberDisplayService extends ContextualDisplayService<PhoneNumberDisplayDefaultKeys> {
  private readonly configService = inject(ConfigurationService);
  private readonly clientAppId = inject(CLIENT_APP_ID);
  private readonly displayContext =
    inject(PHONE_NUMBER_DISPLAY_CONTEXT, { optional: true }) ||
    PhoneNumberDisplayDefaultKeys.ContextualDefaultValue;

  constructor() {
    super(
      ['GlobalApplicativeConstants', 'phoneNumberSeparatorStrategy'],
      PhoneNumberDisplayDefaultKeys.PhoneCountryDefaultValue,
      PhoneNumberDisplayDefaultKeys.ClientAppDefaultValue,
      PhoneNumberDisplayDefaultKeys.ContextualDefaultValue
    );
  }

  protected convertToPhoneNumber(phone: PhoneNumber | string): PhoneNumber {
    return typeof phone === 'object' ? phone : { number: phone };
  }

  protected getPhoneNumberLib(phone: PhoneNumber): LibPhoneNumJS | null {
    const countryCode =
      phone.countryCode ||
      this.configService.getDefaultPhoneNumberCountry() ||
      'fr';

    let phoneObj: LibPhoneNumJS | null;
    try {
      phoneObj = parsePhoneNumber(
        phone.number,
        countryCode.toUpperCase() as CountryCode
      );
    } catch (_) {
      phoneObj = null;
      if (phone.internationalCode) {
        let reducePhone = 0;
        let keepSearching = true;
        while (keepSearching) {
          try {
            phoneObj = parsePhoneNumber(
              phone.internationalCode + phone.number.slice(reducePhone)
            );

            keepSearching = false;
          } catch (__) {
            reducePhone++;
            if (reducePhone >= phone.number.length) {
              phoneObj = null;
              keepSearching = false;
            }
          }
        }
      }
    }

    return phoneObj;
  }

  protected getPhoneNumberCountry(
    phone: PhoneNumber | LibPhoneNumJS | null
  ): string {
    const configDefaultCountry =
      this.configService.getDefaultPhoneNumberCountry();

    if (!phone) {
      return configDefaultCountry;
    }

    if ('nationalNumber' in phone) {
      if (phone.isValid()) {
        return phone.country || configDefaultCountry;
      } else {
        return configDefaultCountry;
      }
    }

    const phoneObj = this.getPhoneNumberLib(phone);
    const defaultCountryCode = phone.countryCode || configDefaultCountry;

    return phoneObj?.country || defaultCountryCode;
  }

  getPhoneNumberSeparatorKey(
    phone: PhoneNumber | string,
    overrideContext?: PhoneNumberDisplayContext
  ): Observable<string> {
    const convertedPhone = this.convertToPhoneNumber(phone);

    const phoneCountry = this.getPhoneNumberCountry(convertedPhone);

    return this.getContextualKey(
      phoneCountry,
      this.clientAppId || PhoneNumberDisplayDefaultKeys.ClientAppDefaultValue,
      overrideContext ||
        this.displayContext ||
        PhoneNumberDisplayDefaultKeys.ContextualDefaultValue
    );
  }

  getInstantPhoneNumberSeparatorKey(
    phone: PhoneNumber | string,
    overrideContext?: PhoneNumberDisplayContext
  ): string {
    const convertedPhone = this.convertToPhoneNumber(phone);

    const phoneCountry = this.getPhoneNumberCountry(convertedPhone);
    return this.getInstantContextualKey(
      phoneCountry,
      this.clientAppId || PhoneNumberDisplayDefaultKeys.ClientAppDefaultValue,
      overrideContext ||
        this.displayContext ||
        PhoneNumberDisplayDefaultKeys.ContextualDefaultValue
    );
  }

  getPhoneNumberSeparatorTranslation(
    phone: PhoneNumber | string,
    overrideContext?: PhoneNumberDisplayContext,
    interpolateParams?: any
  ): Observable<any> {
    const convertedPhone = this.convertToPhoneNumber(phone);

    const phoneCountry = this.getPhoneNumberCountry(convertedPhone);

    return this.getContextualTranslation(
      [
        phoneCountry,
        this.clientAppId || PhoneNumberDisplayDefaultKeys.ClientAppDefaultValue,
        overrideContext ||
          this.displayContext ||
          PhoneNumberDisplayDefaultKeys.ContextualDefaultValue,
      ],
      {
        ...convertedPhone,
        ...interpolateParams,
      }
    );
  }

  getInstantPhoneNumberSeparatorTranslation(
    phone: PhoneNumber | string,
    overrideContext?: PhoneNumberDisplayContext,
    interpolateParams?: any
  ): any {
    const convertedPhone = this.convertToPhoneNumber(phone);

    const phoneCountry = this.getPhoneNumberCountry(convertedPhone);

    return this.getInstantContextualTranslation(
      [
        phoneCountry,
        this.clientAppId || PhoneNumberDisplayDefaultKeys.ClientAppDefaultValue,
        overrideContext ||
          this.displayContext ||
          PhoneNumberDisplayDefaultKeys.ContextualDefaultValue,
      ],
      {
        ...convertedPhone,
        ...interpolateParams,
      }
    );
  }

  getPhoneNumberTranslation(
    phone: PhoneNumber | string,
    overrideContext?: PhoneNumberDisplayContext,
    interpolateParams?: any
  ): Observable<any> {
    const convertedPhone = this.convertToPhoneNumber(phone);

    const phoneObj = this.getPhoneNumberLib(convertedPhone);

    return this.getPhoneNumberSeparatorTranslation(
      phone,
      overrideContext,
      interpolateParams
    ).pipe(
      map(
        (separator) =>
          phoneObj?.formatNational().replace(/ /g, separator) ||
          convertedPhone.number
      )
    );
  }

  getInstantPhoneNumberTranslation(
    phone: PhoneNumber | string,
    options?: {
      isLocal?: boolean;
      overrideContext?: PhoneNumberDisplayContext;
      interpolateParams?: any;
    }
  ): any {
    const convertedPhone = this.convertToPhoneNumber(phone);

    const phoneObj = this.getPhoneNumberLib(convertedPhone);

    const separator = this.getInstantPhoneNumberSeparatorTranslation(
      phone,
      options?.overrideContext,
      options?.interpolateParams
    );

    return (
      (options?.isLocal
        ? phoneObj?.formatNational().replace(/ /g, separator)
        : phoneObj?.formatInternational().replace(/ /g, separator)) ||
      convertedPhone.number
    );
  }

  convertLibPhoneNumberCountryToCountryCode(
    country: CountryCode | undefined,
    value: string
  ): Country {
    // Special case for Greece as libphonenumber-js uses 'GR' while we use 'el'
    if (country === 'GR') {
      return 'el';
    }

    if (
      !country ||
      allowedCountries.includes(country.toLowerCase() as Country) ||
      !!getMatchingPhonePattern(
        value,
        this.configService.getDefaultPhoneNumberCountry().toString()
      )
    ) {
      return this.configService.getDefaultPhoneNumberCountry();
    }

    return country.toLowerCase() as Country;
  }
}
